import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CamadaSync,
  EstadoDaCamada,
  EstadoDaSincronizacao,
  ExecucaoDeSync,
  HistoricoDeSync,
  StatusCompras,
  SyncEmAndamento,
  dispararSyncCompras,
  fetchHistoricoDeSync,
} from '../../../api/comprasDashboardApi';
import { BlocoCarregado } from '../hooks/useComprasDashboard';
import { duracaoDesde } from './CarimboSync';
import FaixaEstadoDaSincronizacao from './FaixaEstadoDaSincronizacao';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { useAlcanca } from '../../../hooks/usePapeis';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Aba "Sincronização" do Dashboard de Compras (at-100).
 *
 * Existe porque **quem lê o gráfico precisa saber o quão fresco é o dado** — e, antes desta aba, a
 * única resposta era o carimbo de uma linha no topo. O que ela acrescenta: quanto a última
 * sincronização levou, se deu certo, o histórico das anteriores, e o que está acontecendo agora.
 *
 * 🔴 **Três estados, nunca dois** (a mesma regra do carimbo, herdada do Comissionamentos): dado,
 * "ainda não sincronizado" e "não foi possível verificar". Falha de carga aqui é especialmente cara
 * — uma tela que dissesse "nunca sincronizado" por causa de um 500 mandaria alguém investigar uma
 * integração que está funcionando.
 *
 * 🔴 **"Trabalhando" e "travado" não podem se parecer.** A distinção é o batimento (`ultimoSinal`),
 * nunca o horário de início: uma varredura longa e saudável tem início antigo. Foi o defeito vivido
 * em 11/09/2026 — um job com 1.615 registros já gravados reportando `paginas = 0`.
 */

const ROTULOS: Record<CamadaSync, { nome: string; oQueTraz: string }> = {
  QUENTE: { nome: 'Requisições em aberto', oQueTraz: 'pendentes, em cotação e em negociação' },
  MORNA: { nome: 'Requisições em fechamento', oQueTraz: 'com pedido gerado e parcialmente atendidas' },
  FRIA: { nome: 'Base completa', oQueTraz: 'todas as requisições, com os itens' },
  PEDIDOS: { nome: 'Pedidos de compra', oQueTraz: 'os pedidos dos últimos 12 meses' },
  // at-109. O rótulo diz "origem" porque é para isso que ela existe: sem esta camada, o pedido de
  // compra não tem comprador, placa nem oficina — o ERP não guarda nenhum dos três no pedido.
  NEGOCIACOES: {
    nome: 'Origem dos pedidos',
    oQueTraz: 'as negociações, que ligam cada pedido à requisição que o originou',
  },
  CADASTROS: { nome: 'Cadastros', oQueTraz: 'fornecedores e locais de estoque' },
  // ⚠️ O rótulo diz "oficina interna" porque é o escopo real do que essa camada alimenta: a peça
  // que vai para oficina externa é avisada em grupo de WhatsApp e nunca entra no ERP. A ressalva
  // aparece junto do indicador; aqui ela evita que alguém leia o cartão como "todas as entregas".
  // at-128: a camada passou a ler o elo pedido → nota e a data de entrada de cada nota.
  ENTREGAS: {
    nome: 'Entregas (oficina interna)',
    oQueTraz: 'a ligação de cada pedido às notas de entrada, e a data de entrada de cada nota',
  },
};

/**
 * 🔴 Derivada de `ROTULOS`, que é `Record<CamadaSync, …>` — camada nova no tipo não compila sem
 * rótulo, e daí entra nesta lista sozinha.
 *
 * Era literal e ficou sem `ENTREGAS` quando a camada nasceu (achado em 13/09/2026): a aba mostrava
 * 5 cartões e escondia justamente a que alimenta "Entrega no prazo" — o usuário não tinha como
 * saber se aquele indicador estava fresco, atrasado ou nunca executado.
 */
const CAMADAS = Object.keys(ROTULOS) as CamadaSync[];

/** Acima disto, um job em andamento deixa de ser "lento" e passa a ser "sem sinal". */
const SEGUNDOS_SEM_SINAL_PARA_ALERTAR = 180;

/**
 * 🔴 A régua do frescor é RELATIVA à cadência de cada camada, nunca um prazo único.
 *
 * "Atualizado há 4 h" é atraso na Base completa, que promete 30 minutos, e é perfeitamente normal
 * nos Cadastros, que rodam uma vez por dia. Um limiar único colocaria as duas na mesma cor e
 * tornaria o selo inútil justamente onde ele decide alguma coisa.
 *
 * Os multiplicadores são escolha de apresentação e estão aqui; a **cadência** vem do backend
 * (`cadenciaSegundos`), que é o dono dos crons. 3x dá folga para um ciclo perdido sem alarme —
 * varredura que colide no claim é rotina —, e 8x é onde a defasagem passa a mudar o que o painel
 * mostra.
 */
const FATOR_ATENCAO = 3;
const FATOR_ATRASO = 8;

export type Frescor =
  | 'EM_DIA'
  | 'ATENCAO'
  | 'ATRASADO'
  | 'PAUSADO'
  | 'FORA_DO_HORARIO'
  | 'SO_MANUAL'
  | 'DESCONHECIDO';

/**
 * Quão fresco está o dado desta camada, comparado ao ritmo que ela promete.
 *
 * ⚠️ Mede o último **sucesso**, nunca a última tentativa: é o sucesso que datou o que está na tela.
 * Sem sucesso nenhum, devolve `DESCONHECIDO` — "não sei" não é "atrasado", e pintar de vermelho uma
 * camada recém-implantada afirmaria um atraso que ninguém mediu.
 *
 * 🔴 **Fora do expediente vem ANTES da régua de idade** (at-103), e a ordem é o ponto: das 19h de
 * sexta às 8h de segunda a camada Quente acumula 61 horas de idade contra uma cadência de 60
 * segundos, então a régua a chamaria de "Atrasada" — vermelho de falha, numa tela cuja função é
 * dizer se a integração está saudável, para um sync parado exatamente como foi projetado.
 *
 * ⚠️ Quem decide que estamos fora do horário é o **servidor** (`paradaPorHorario`): ele é dono dos
 * crons e do fuso. Recalcular aqui seria uma segunda definição do mesmo corte, no fuso do navegador.
 */
export function frescorDaCamada(
  estado: Pick<
    EstadoDaCamada,
    'ultimoSucesso' | 'cadenciaSegundos' | 'paradaPorHorario' | 'soManual'
  > & {
    /** A sincronização inteira está pausada (at-108). */
    pausado?: boolean;
  },
  agora = Date.now(),
): Frescor {
  /*
   * at-125: camada sem agendamento (só disparo manual) não tem cadência a cumprir. A régua de
   * idade a pintaria de "Atrasado" para sempre, sem nada errado. Vem antes de tudo — inclusive de
   * "nunca sincronizado", que para ela é estado normal.
   */
  if (estado.soManual) return 'SO_MANUAL';
  if (!estado.ultimoSucesso || !estado.cadenciaSegundos) return 'DESCONHECIDO';
  /*
   * 🔴 A pausa vem ANTES da régua de idade, pelo mesmo motivo que "fora do horário" vem (at-103):
   * pausado por um dia, a camada quente acumula 1.440 vezes a cadência que promete, e a régua a
   * chamaria de "Atrasado" — vermelho de falha para um sync parado exatamente como o ADMIN mandou.
   *
   * ⚠️ E vem antes de `paradaPorHorario` porque a explicação de uma pausa é outra, e leva a outra
   * ação: fora do horário o dado volta sozinho na abertura; pausado, alguém precisa retomar.
   */
  if (estado.pausado) return 'PAUSADO';
  if (estado.paradaPorHorario) return 'FORA_DO_HORARIO';
  const idade = (agora - new Date(estado.ultimoSucesso).getTime()) / 1000;
  if (idade <= estado.cadenciaSegundos * FATOR_ATENCAO) return 'EM_DIA';
  if (idade <= estado.cadenciaSegundos * FATOR_ATRASO) return 'ATENCAO';
  return 'ATRASADO';
}

const SELO_DO_FRESCOR: Record<Frescor, { classe: string; texto: string } | null> = {
  EM_DIA: { classe: 'badge-verde', texto: 'Em dia' },
  ATENCAO: { classe: 'badge-laranja', texto: 'Atrasando' },
  ATRASADO: { classe: 'badge-vermelho', texto: 'Atrasado' },
  // Cinza: não é atraso nem degradação, é a leitura parada por decisão de quem administra. A cor
  // de alerta aqui ensinaria quem lê a ignorar os selos que importam.
  PAUSADO: { classe: 'badge-cinza', texto: 'Pausado' },
  // Azul, nunca laranja nem vermelho: não é atraso nem degradação, é o sync parado de propósito.
  // A cor de alerta aqui treinaria quem lê a ignorar os selos que importam.
  FORA_DO_HORARIO: { classe: 'badge-azul', texto: 'Fora do horário' },
  // Cinza, como a pausa: a camada não roda sozinha por decisão, não por falha (at-125).
  SO_MANUAL: { classe: 'badge-cinza', texto: 'Só manual' },
  // Sem selo: a linha "Ainda não sincronizado" já diz o que há para dizer, e um selo cinza ao lado
  // dela só repetiria a mesma informação com mais tinta.
  DESCONHECIDO: null,
};

const POLL_RAPIDO_MS = 10_000;
const POLL_NORMAL_MS = 60_000;

export function formatarDuracao(segundos: number | null): string | null {
  if (segundos === null || segundos === undefined) return null;
  if (segundos < 60) return `${segundos}s`;
  const min = Math.floor(segundos / 60);
  const seg = segundos % 60;
  if (min < 60) return seg ? `${min}min ${seg}s` : `${min}min`;
  const horas = Math.floor(min / 60);
  return `${horas}h ${min % 60}min`;
}

/**
 * Quanto ainda falta, estimado pelo ritmo das etapas já concluídas.
 *
 * ⚠️ Devolve `null` sem etapas declaradas ou na primeira etapa: com uma amostra só, a "estimativa"
 * seria o palpite de que todas as etapas custam igual — e um número inventado numa tela de frescor
 * de dado é pior que a ausência dele. Quem chama rotula como estimativa.
 */
export function estimarRestanteSegundos(
  emAndamento: SyncEmAndamento,
  agora = Date.now(),
): number | null {
  const { etapaAtual, etapasTotal, desde } = emAndamento;
  if (!etapaAtual || !etapasTotal || etapaAtual < 2 || etapaAtual > etapasTotal) return null;
  const decorrido = (agora - new Date(desde).getTime()) / 1000;
  if (decorrido <= 0) return null;
  const concluidas = etapaAtual - 1;
  const porEtapa = decorrido / concluidas;
  return Math.round(porEtapa * (etapasTotal - concluidas));
}

function segundosDesde(iso: string | null, agora = Date.now()): number | null {
  if (!iso) return null;
  return Math.round((agora - new Date(iso).getTime()) / 1000);
}

function BadgeDoDesfecho({ execucao }: { execucao: ExecucaoDeSync }) {
  if (execucao.status === 'EM_ANDAMENTO') {
    return <span className="cilia-badge badge-azul">Em andamento</span>;
  }
  if (execucao.status === 'FALHOU') {
    return <span className="cilia-badge badge-vermelho">Falhou</span>;
  }
  // Parcial é desfecho próprio: concluiu, mas não trouxe tudo. Mostrá-lo como sucesso esconderia
  // exatamente o caso em que o painel está incompleto sem ninguém saber.
  if (execucao.parcial) {
    return <span className="cilia-badge badge-laranja">Parcial</span>;
  }
  return <span className="cilia-badge badge-verde">Concluída</span>;
}

export function CartaoDaCamada({
  camada,
  estado,
  janela,
  sincronizacao,
}: {
  camada: CamadaSync;
  estado: EstadoDaCamada;
  janela: string;
  sincronizacao?: EstadoDaSincronizacao;
}) {
  const rotulo = ROTULOS[camada];
  const emAndamento = estado.emAndamento;
  const semSinalHa = emAndamento ? segundosDesde(emAndamento.ultimoSinal) : null;
  const travada = semSinalHa !== null && semSinalHa > SEGUNDOS_SEM_SINAL_PARA_ALERTAR;
  const restante = emAndamento ? estimarRestanteSegundos(emAndamento) : null;
  const pausado = sincronizacao?.pausado === true;
  const emRitmoDiario = sincronizacao?.modo === 'DIARIO';
  const selo = estado.nuncaExecutada && !estado.soManual
    ? null
    : SELO_DO_FRESCOR[frescorDaCamada({ ...estado, pausado })];

  return (
    <div className="do-card compras-sync-cartao">
      <div className="compras-sync-cartao-topo">
        <strong>{rotulo.nome}</strong>
        {/*
          Um selo só, e o da execução em curso tem precedência: enquanto a camada está varrendo, o
          que interessa é isso — o frescor está prestes a mudar de qualquer forma.
        */}
        {emAndamento ? (
          <span className={`cilia-badge ${travada ? 'badge-laranja' : 'badge-azul'}`}>
            {travada ? 'Sem sinal' : 'Sincronizando'}
          </span>
        ) : selo ? (
          <span className={`cilia-badge ${selo.classe}`}>{selo.texto}</span>
        ) : null}
      </div>
      <p className="do-nota-janela">Traz {rotulo.oQueTraz}.</p>

      {estado.nuncaExecutada ? (
        <p className="compras-sync-linha">Ainda não sincronizado.</p>
      ) : (
        <>
          <p className="compras-sync-linha">
            Atualizado {duracaoDesde(estado.ultimoSucesso) ?? 'em momento desconhecido'}
            {formatarDuracao(estado.duracaoSegundos)
              ? ` · levou ${formatarDuracao(estado.duracaoSegundos)}`
              : ''}
            {estado.linhas !== null ? ` · ${estado.linhas.toLocaleString('pt-BR')} registros` : ''}
          </p>
          {/*
            🔴 O selo "Fora do horário" sozinho não explica nada — quem lê precisa saber que o dado
            NÃO vai envelhecer indefinidamente e quando ele volta a ser atualizado. Sem esta linha,
            a leitura mais provável de uma camada parada é "a integração caiu".

            Nota discreta, nunca `aviso-de-tela`: não há nada errado acontecendo.
          */}
          {estado.soManual ? (
            <p className="do-nota-janela">
              Esta leitura não roda sozinha: só quando alguém a dispara aqui.
            </p>
          ) : null}
          {estado.paradaPorHorario ? (
            <p className="do-nota-janela">
              Esta leitura roda {janela}. Fora desse horário o movimento de compras é praticamente
              nulo, e o dado volta a ser atualizado na próxima abertura.
            </p>
          ) : null}
          {/*
            at-108 — as duas notas que substituem a de expediente, cada uma dizendo QUANDO o dado
            volta a mudar. Sem isso, o selo sozinho ("Pausado") deixa quem lê sem saída.

            ⚠️ São excludentes com a de expediente por construção: o backend zera `paradaPorHorario`
            quando há pausa ou ritmo diário, porque dois avisos simultâneos mandariam o usuário a
            lugares diferentes.
          */}
          {pausado ? (
            <p className="do-nota-janela">
              A sincronização está pausada, então esta leitura não roda — e o dado acima continua
              como está até alguém retomar em Administração do Sistema.
            </p>
          ) : emRitmoDiario ? (
            <p className="do-nota-janela">
              Esta leitura roda uma vez ao dia, às {sincronizacao?.horaDaLeituraDiaria}h. Se o
              sistema estiver fora do ar nesse horário, ela acontece assim que ele voltar.
            </p>
          ) : null}
          {/*
            Aviso do último SUCESSO e erro da última TENTATIVA são coisas diferentes, e as duas
            precisam aparecer: a primeira diz que o dado exibido está incompleto, a segunda que a
            tentativa mais recente não trouxe nada (e que o dado é mais velho do que parece).
          */}
          {estado.parcial && estado.avisoDoUltimoSucesso ? (
            <p className="aviso-de-tela">Veio incompleto: {estado.avisoDoUltimoSucesso}</p>
          ) : null}
          {/*
            🔴 O peso segue a CAUSA, e nunca usa `erro-de-tela` — aquela classe é o vermelho de
            "esta tela falhou ao carregar", e usá-la aqui daria à interrupção mais rotineira do
            sistema a mesma silhueta de uma tela quebrada. Em produção isso aconteceria a cada
            deploy que pegasse a Base completa no meio (513s de janela), que é a receita para
            ensinar o usuário a ignorar vermelho.

            Abandonada: nota discreta — o processo morreu, o saneamento já devolveu a camada à fila
            e nada precisa ser feito. Falha real (o ERP recusou, a rede caiu): aviso laranja, porque
            aí alguém pode precisar agir.
          */}
          {estado.erro ? (
            estado.ultimaTentativaAbandonada ? (
              <p className="do-nota-janela">
                A tentativa de {duracaoDesde(estado.ultimaTentativa) ?? 'há pouco'} foi interrompida
                antes de concluir, e a camada já voltou para a fila.
              </p>
            ) : (
              <p className="aviso-de-tela">
                A tentativa de {duracaoDesde(estado.ultimaTentativa) ?? 'há pouco'} não trouxe
                dados: {estado.erro}
              </p>
            )
          ) : null}
        </>
      )}

      {emAndamento ? (
        <div className="compras-sync-progresso">
          <p className="compras-sync-linha">
            {emAndamento.etapa ?? 'Em preparação'}
            {emAndamento.etapaAtual && emAndamento.etapasTotal
              ? ` · etapa ${emAndamento.etapaAtual} de ${emAndamento.etapasTotal}`
              : ''}
            {emAndamento.paginas ? ` · ${emAndamento.paginas} páginas lidas` : ''}
          </p>
          {emAndamento.etapaAtual && emAndamento.etapasTotal ? (
            <div className="compras-sync-barra" aria-hidden="true">
              <div
                className="compras-sync-barra-preenchida"
                style={{
                  width: `${Math.min(100, Math.round((emAndamento.etapaAtual / emAndamento.etapasTotal) * 100))}%`,
                }}
              />
            </div>
          ) : null}
          <p className="do-nota-janela">
            Começou {duracaoDesde(emAndamento.desde) ?? 'há pouco'}
            {restante !== null ? ` · estimativa de mais ${formatarDuracao(restante)}` : ''}
            {restante === null ? ' · ainda sem base para estimar o tempo restante' : ''}
          </p>
          {travada ? (
            <p className="aviso-de-tela">
              Sem sinal há {formatarDuracao(semSinalHa)}. A sincronização pode ter travado — se
              persistir, acione a TI.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  status: BlocoCarregado<StatusCompras>;
  recarregarStatus: () => void;
}

/**
 * A carga histórica dos itens de pedido (at-109) — ação pontual, feita uma vez após o deploy.
 *
 * 🔴 Existe porque o sync horário só traz os itens dos pedidos dos **últimos 3 dias**: sem esta
 * carga, os agrupamentos da aba Pedidos contariam zero itens no histórico, e o filtro de setor não
 * valeria para pedido nenhum. Com itens, a varredura de 12 meses passa de ~46 min para ~3 h — é
 * por isso que ela é manual, e não mais um cron.
 *
 * ⚠️ **Só ADMIN**, porque a rota de disparo é `@Roles(ADMIN)` e esta tela é ADMIN e COMPRA.
 * Mostrar o botão a quem recebe 403 é erro de UX, não de segurança (ADR 0012).
 */
function CargaHistoricaDeItens() {
  const ehAdmin = useAlcanca(['ADMIN']);
  const [confirmando, setConfirmando] = useState(false);
  const [disparando, setDisparando] = useState(false);

  if (!ehAdmin) return null;

  const disparar = async () => {
    setDisparando(true);
    try {
      const r = await dispararSyncCompras('PEDIDOS', true);
      if (r.disparado) {
        notify.success(
          'Carga histórica iniciada. Ela leva cerca de três horas; acompanhe nesta aba, em "Pedidos de compra".',
        );
      } else {
        // Não é erro: ou já há uma varredura em curso, ou o sync está pausado. Dizer o motivo
        // evita o clique repetido.
        notify.warning(r.motivo ?? 'A carga não foi iniciada.', { toastId: 'carga-itens' });
      }
    } catch (erro) {
      // `subject` é SUBSTANTIVO MASCULINO SINGULAR — o helper conjuga o verbo a partir do `action`
      // e sempre no masculino (regra da at-65, BUG-V51).
      notify.actionError(erro, { action: 'send', subject: 'pedido de carga histórica' });
    } finally {
      setDisparando(false);
      setConfirmando(false);
    }
  };

  return (
    <div className="do-grid-largo">
      <div className="do-card">
        <h3>Carga histórica dos itens de pedido</h3>
        <p className="do-nota-janela">
          A sincronização de hora em hora traz os itens dos pedidos dos últimos três dias. Esta
          carga busca os itens dos <strong>12 meses</strong> anteriores — é o que faz a aba Pedidos
          contar itens no histórico e o filtro de setor valer para pedidos. Precisa ser feita uma
          vez só, e leva cerca de três horas.
        </p>
        <button
          type="button"
          className="do-btn-filtros"
          disabled={disparando}
          onClick={() => setConfirmando(true)}
        >
          {disparando ? 'Iniciando...' : 'Buscar itens dos últimos 12 meses'}
        </button>
      </div>

      {confirmando && (
        <ConfirmModal
          isOpen
          title="Buscar os itens dos pedidos de 12 meses?"
          message={
            'A busca leva cerca de três horas e consome a API do Nasajon durante todo esse tempo. ' +
            'As demais sincronizações continuam funcionando. Faça isto uma vez, após a atualização do sistema.'
          }
          confirmLabel="Iniciar a carga"
          onConfirm={disparar}
          onCancel={() => setConfirmando(false)}
        />
      )}
    </div>
  );
}

export default function PainelSincronizacao({ status, recarregarStatus }: Props) {
  const [historico, setHistorico] = useState<BlocoCarregado<HistoricoDeSync>>({
    dados: null,
    carregando: true,
    erro: false,
  });

  const carregar = useCallback(async () => {
    try {
      const dados = await fetchHistoricoDeSync();
      setHistorico({ dados, carregando: false, erro: false });
    } catch {
      // 🔴 `dados: null`: histórico vazio por erro seria lido como "nunca houve sincronização".
      setHistorico({ dados: null, carregando: false, erro: true });
    }
  }, []);

  const temJobEmCurso = Boolean(
    status.dados && CAMADAS.some((c) => status.dados?.camadas[c]?.emAndamento),
  );
  // O ritmo do polling entra num ref para a troca de cadência não reiniciar o timer a cada render.
  const emCursoRef = useRef(temJobEmCurso);
  emCursoRef.current = temJobEmCurso;

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    const timer = setInterval(
      () => {
        void carregar();
        recarregarStatus();
      },
      temJobEmCurso ? POLL_RAPIDO_MS : POLL_NORMAL_MS,
    );
    return () => clearInterval(timer);
  }, [carregar, recarregarStatus, temJobEmCurso]);

  if (status.carregando && !status.dados) {
    return <p className="compras-sync-linha">Verificando o estado da sincronização...</p>;
  }

  if (status.erro || !status.dados) {
    return (
      <p className="erro-de-tela">
        Não foi possível verificar o estado da sincronização. Isto não significa que ela falhou —
        significa que não conseguimos perguntar. Recarregue a página; se persistir, acione a TI.
      </p>
    );
  }

  if (!status.dados.configurado) {
    return (
      <p className="erro-de-tela">
        A integração com o Nasajon não está configurada neste ambiente, então nada é sincronizado.
        Acione a TI.
      </p>
    );
  }

  return (
    <>
      <FaixaEstadoDaSincronizacao
        sincronizacao={status.dados.sincronizacao}
        ultimoDadoEm={status.dados.camadas.QUENTE?.ultimoSucesso}
      />

      <p className="do-nota-janela">
        Cada parte do painel é lida do Nasajon no seu próprio ritmo. Abaixo, quando cada uma foi
        atualizada pela última vez, quanto levou e o que está acontecendo agora.
      </p>

      <div className="do-grid compras-sync-grid">
        {/*
          ⚠️ Renderiza só o que a resposta trouxe. Camada que o backend não devolve é OMITIDA, nunca
          desenhada com estado vazio — e, sobretudo, não pode derrubar a aba: sem este filtro, um
          backend anterior à camada nova (a janela da amarra "backend antes do frontend") faz o
          cartão ler `undefined.emAndamento` e a aba inteira vira tela branca. Trocar o frescor de
          uma camada por nenhuma informação é o pior desfecho possível numa tela cuja única função é
          informar frescor.
        */}
        {CAMADAS.filter((camada) => status.dados!.camadas[camada]).map((camada) => (
          <CartaoDaCamada
            key={camada}
            camada={camada}
            estado={status.dados!.camadas[camada]}
            // Fallback pelo mesmo motivo do filtro acima: backend anterior à at-103 não manda o
            // texto, e a nota não pode sair com "undefined" no meio da frase.
            janela={status.dados!.janelaDeExpediente ?? 'em horário comercial'}
            sincronizacao={status.dados!.sincronizacao}
          />
        ))}
      </div>

      <CargaHistoricaDeItens />

      <div className="do-grid-largo">
        <div className="do-card">
          <h3>Execuções recentes</h3>
          {historico.carregando ? (
            <p className="compras-sync-linha">Carregando o histórico...</p>
          ) : historico.erro || !historico.dados ? (
            <p className="erro-de-tela">
              Não foi possível carregar o histórico de sincronizações. Recarregue a página; se
              persistir, acione a TI.
            </p>
          ) : historico.dados.execucoes.length === 0 ? (
            <p className="compras-sync-linha">
              Nenhuma sincronização registrada ainda. O registro começa a partir desta versão.
            </p>
          ) : (
            <div className="do-tabela-wrapper">
              <table className="do-tabela">
                <thead>
                  <tr>
                    <th>Parte</th>
                    <th>Quando</th>
                    <th>Duração</th>
                    <th>Registros</th>
                    <th>Mudanças</th>
                    <th>Desfecho</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.dados.execucoes.map((e) => (
                    <tr key={e.id}>
                      <td title={ROTULOS[e.camada]?.nome ?? e.camada}>
                        {ROTULOS[e.camada]?.nome ?? e.camada}
                      </td>
                      <td>{new Date(e.iniciadoEm).toLocaleString('pt-BR')}</td>
                      <td>
                        {formatarDuracao(e.duracaoSegundos) ??
                          (e.status === 'EM_ANDAMENTO' ? 'em curso' : '-')}
                      </td>
                      <td>{e.linhas.toLocaleString('pt-BR')}</td>
                      <td>{e.diff.toLocaleString('pt-BR')}</td>
                      <td>
                        <BadgeDoDesfecho execucao={e} />
                      </td>
                      {/* Célula de uma linha, com o texto inteiro no hover (regra de 12/08/2026). */}
                      <td className="do-codigo compras-sync-observacao" title={e.erro ?? ''}>
                        {e.erro ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
